"use client";

import { useState, type SubmitEvent } from "react";
import { Button, IconButton } from "./button";
import { MessageSquare, Icon } from "./icon";
import { Modal } from "./modal";
import { Textarea } from "./textarea";

type Status = "idle" | "sending" | "sent" | "error";

/** Compact signed-in nav action that sends product feedback to the support inbox. */
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  function close() {
    if (status === "sending") return;
    setOpen(false);
    setStatus("idle");
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "sending" || message.trim() === "") return;
    setStatus("sending");
    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
      if (!response.ok) throw new Error("request failed");
      setMessage("");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      <IconButton
        label="Send feedback"
        tooltip="below"
        variant="secondary"
        size="sm"
        className="pz-feedback__trigger"
        onClick={() => setOpen(true)}
      >
        <Icon icon={MessageSquare} size="md" />
      </IconButton>
      <Modal
        open={open}
        onClose={close}
        title="Send feedback"
        footer={
          status === "sent" ? (
            <Button onClick={close}>Done</Button>
          ) : (
            <Button type="submit" form="feedback-form" loading={status === "sending"}>
              Send feedback
            </Button>
          )
        }
      >
        {status === "sent" ? (
          <p className="pz-feedback__status">Thanks — your feedback was sent.</p>
        ) : (
          <form id="feedback-form" className="pz-feedback__form" onSubmit={(event) => void submit(event)}>
            <label htmlFor="feedback-message">What would you like us to know?</label>
            <Textarea
              id="feedback-message"
              required
              rows={5}
              maxLength={2000}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Share an idea, report a problem, or tell us what could be better."
            />
            {status === "error" ? (
              <p className="pz-feedback__error" role="alert">
                Something went wrong — please try again.
              </p>
            ) : null}
          </form>
        )}
      </Modal>
    </>
  );
}
